import { config } from '../config';
import { store } from '../store';
import { LivePlaybookItem } from '../store/slices/salesPlaybookSlice';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { HotButtonCard, setHotButton } from '../store/slices/hotButtonSlice';
import { applyLivePlaybookFrame } from './livePlaybookFrames';

interface ChecklistMessage {
  type: 'playbook_snapshot' | 'playbook_item' | 'hot_button_state' | 'pong';
  data?: {
    sessionId: string;
    playbookName: string;
    role: string;
    items?: LivePlaybookItem[];
    section?: LivePlaybookItem['section'];
    id?: string;
    checked?: boolean;
    note?: string;
    source?: 'ai' | 'human';
    card?: HotButtonCard | null;
    cards?: HotButtonCard[];
    addedCardId?: string;
    enabled?: boolean;
    justChecked?: boolean;
    justNoted?: boolean;
  };
}

class PlaybookChecklistWebSocketService {
  private socket: WebSocket | null = null;
  private roomId: string | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private generation = 0;
  private retries = 0;

  async connect(roomId: string): Promise<void> {
    if (this.roomId === roomId && this.socket &&
        (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    this.disconnect();
    this.roomId = roomId;
    this.retries = 0;
    await this.open(this.generation);
  }

  private async open(generation: number): Promise<void> {
    const { token } = await validateAndRefreshToken();
    if (generation !== this.generation || !this.roomId) return;
    if (!token) {
      this.scheduleRetry(generation);
      return;
    }
    const url = new URL(config.server.playbookWsUrl);
    url.searchParams.set('room_id', this.roomId);
    url.searchParams.set('token', token);
    const socket = new WebSocket(url.toString());
    this.socket = socket;
    socket.onopen = () => {
      if (generation !== this.generation) return;
      this.retries = 0;
      this.pingTimer = setInterval(() => this.send({ type: 'ping' }), 30000);
    };
    socket.onmessage = (event) => {
      if (generation !== this.generation) return;
      try {
        const message: ChecklistMessage = JSON.parse(event.data);
        const data = message.data;
        if (!data || data.sessionId !== this.roomId) return;
        if (message.type === 'hot_button_state') {
          // Hot buttons are gated server-side: only `enabled: true` shows the pane.
          // A frame without the flag (an older server) counts as off.
          if (data.enabled !== true) {
            store.dispatch(setHotButton({ cards: [], enabled: false }));
            return;
          }
          const cards = data.cards ?? (data.card ? [data.card] : []);
          if (Array.isArray(cards) && cards.every(card => typeof card.id === 'string' &&
              typeof card.title === 'string' && typeof card.suggestion === 'string' &&
              typeof card.quote === 'string')) {
            store.dispatch(setHotButton({ cards, addedCardId: data.addedCardId, enabled: true }));
          }
          return;
        }
        applyLivePlaybookFrame(store.dispatch, message.type, data);
      } catch (error) {
        console.warn('[PlaybookChecklist] Invalid message', error);
      }
    };
    socket.onclose = (event) => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (generation !== this.generation || event.code === 1008) return;
      this.scheduleRetry(generation);
    };
    socket.onerror = () => socket.close();
  }

  private scheduleRetry(generation: number): void {
    if (generation !== this.generation || this.retries >= 10 || !this.roomId) return;
    const backoffMs = Math.min(1000 * 2 ** this.retries, 30000);
    const delayMs = Math.round(backoffMs * (0.5 + Math.random()));
    this.retries += 1;
    this.retryTimer = setTimeout(() => void this.open(generation), delayMs);
  }

  refresh(): void {
    this.send({ type: 'refresh' });
  }

  dismissHotButton(cardId: string): void {
    this.send({ type: 'dismiss_hot_button', cardId });
  }

  private send(message: { type: string; cardId?: string }): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  disconnect(): void {
    const hadSession = this.roomId !== null;
    this.generation += 1;
    this.roomId = null;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.retryTimer = null;
    this.pingTimer = null;
    this.socket?.close(1000);
    this.socket = null;
    if (hadSession) store.dispatch(setHotButton(null));
  }
}

export const playbookChecklistWebSocketService = new PlaybookChecklistWebSocketService();
